import { useEffect, useMemo } from 'react'
import { createFileRoute, defaultParseSearch, redirect } from '@tanstack/react-router'
import { isEqual } from 'es-toolkit'
import {
  customerDocumentsSearchSchema,
  CustomerDocumentsWorkspace,
  mergeCustomerDocumentsCache,
  parseCustomerDocumentsUrl,
  scopeCustomerDocumentsSearch,
  toCustomerDocumentsUrl,
  validateCustomerDocumentsUrl
} from '@/features/customer-documents/index.ts'
import { createListQueryStore } from '@/stores/list-query-store.ts'

export const Route = createFileRoute('/_authenticated/customer-management/customers/$customerId/documents')({
  validateSearch: validateCustomerDocumentsUrl,
  beforeLoad: ({ context, params, search, preload, location }) => {
    const { currentSession } = context

    const canReadOrders =
      currentSession.isSuperAdmin ||
      currentSession.permissions.some(
        (permission) => permission === 'order.rental.read' || permission === 'order.service.read'
      )
    const canReadBills = currentSession.isSuperAdmin || currentSession.permissions.includes('bill.read')

    const customerId = Number(params.customerId)
    if (!Number.isSafeInteger(customerId) || customerId < 1) {
      throw redirect({ to: '/customer-management/customers', search: { current: 1, pageSize: 20 }, replace: true })
    }
    const documentsStore = createListQueryStore(
      `super-mega:customer-documents:${customerId}`,
      customerDocumentsSearchSchema
    )
    const hasExplicitSearch = Object.keys(search).length > 0
    const restored = !hasExplicitSearch
      ? (documentsStore.getState().searches[currentSession.id] ?? {})
      : parseCustomerDocumentsUrl(search)
    const kind = restored.kind ?? (canReadOrders ? 'rental' : 'bills')

    const canonical = toCustomerDocumentsUrl(scopeCustomerDocumentsSearch(customerId, { ...restored, kind }))
    if (!preload && !isEqual(defaultParseSearch(location.searchStr), canonical)) {
      throw redirect({
        to: '/customer-management/customers/$customerId/documents',
        params,
        replace: true,
        search: canonical,
        hash: location.hash
      })
    }
    return { documentsStore, canReadOrders, canReadBills }
  },
  head: () => ({ meta: [{ title: '客户单据 · Super Mega' }] }),
  component: CustomerDocumentsRoute
})

function CustomerDocumentsRoute() {
  const { currentSession, documentsStore, canReadOrders, canReadBills } = Route.useRouteContext()
  const customerId = Number(Route.useParams().customerId)
  const raw = Route.useSearch()
  const navigate = Route.useNavigate()
  // 保持查询引用稳定，避免后台刷新清空列表选择。
  const search = useMemo(
    () => scopeCustomerDocumentsSearch(customerId, parseCustomerDocumentsUrl(raw)),
    [customerId, raw]
  )
  useEffect(() => {
    const state = documentsStore.getState()
    state.remember(
      currentSession.id,
      mergeCustomerDocumentsCache(customerId, search, state.searches[currentSession.id])
    )
  }, [documentsStore, currentSession.id, customerId, search])
  const getSnapshot = () =>
    mergeCustomerDocumentsCache(customerId, search, documentsStore.getState().searches[currentSession.id])
  return (
    <CustomerDocumentsWorkspace
      customerId={customerId}
      currentSession={currentSession}
      search={search}
      canReadOrders={canReadOrders}
      canReadBills={canReadBills}
      onSearchChange={(patch, replace) =>
        navigate({
          replace,
          search: toCustomerDocumentsUrl(scopeCustomerDocumentsSearch(customerId, { ...getSnapshot(), ...patch }))
        })
      }
    />
  )
}
